<script lang="ts">
	import { createForm } from 'felte';
	import { set } from 'lodash-es';
	import { _ } from 'svelte-i18n';
	import { onMount, type ComponentType } from 'svelte';

	import { getHubStores } from '$lib/stores/hubStores';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import Button from '$lib/components/Button/Button.svelte';
	import { Input, TextArea, CheckBox } from '$lib/components/Inputs';

	import type { GetPostReturn } from '$lib/api/rpc';
	import SettingDatePicker from './SettingDatePicker.svelte';
	import { selectedPost as post } from '$lib/components/Post/postCommands';
	import { isPostSettingsModalOpen } from './settingsModalStore';

	const { api, hub } = getHubStores();

	interface Form {
		title: string;
		description: string;
		author: string;
		sourceNet: string;
		sourceURL: string;
		postDate: string;
		externalSource: boolean;
	}

	interface InputType {
		name: keyof Form;
		path: string;
		value: string;
		placeholder: string;
		label: string;
		custom?: boolean | undefined;
		setFields?: (fieldName: string, value: string) => void;
		component: ComponentType;
	}

	let response: GetPostReturn;
	let settingsForm: InputType[] = [];

	const { form, setFields } = createForm<Form>({
		async onSubmit(values) {
			let value: keyof Form;
			for (value in values) {
				const path = settingsForm.find((input) => input.name === value)?.path;
				if (path) set($post, path, values[value]);
			}
			$post = $post;
			try {
				const resp = await api.updatePost({
					hubId: $post.hub_id,
					postId: $post.id,
					postUpdate: $post,
				});
				if (resp) {
					toastMessages.success($_('post.settings.post.save.success'));
				}
				$isPostSettingsModalOpen = false;
			} catch (error) {
				toastMessages.danger('', error);
			}
		},
	});
	onMount(async () => {
		response = await api.getPost({ hubId: $hub.id, postId: $post.id });
		settingsForm = [
			{
				name: 'title',
				path: 'title',
				value: response.post.title || '',
				placeholder: $_('hubs.post.title'),
				label: $_('common.title'),
				component: Input,
			},
			{
				name: 'description',
				path: 'description',
				value: response.post.description || '',
				placeholder: $_('hubs.post.description'),
				label: $_('common.description'),
				component: TextArea,
			},
			{
				name: 'author',
				path: 'author.name',
				value: response.post.author.name || '',
				placeholder: $_('common.author'),
				label: $_('common.author'),
				component: Input,
			},
			{
				name: 'sourceNet',
				path: 'source.network',
				value: response.post.source.network || '',
				placeholder: $_('hubs.source.network'),
				label: $_('hubs.source.network'),
				component: Input,
			},
			{
				name: 'sourceURL',
				path: 'source.url',
				value: response.post.source.url || '',
				placeholder: $_('hubs.source.url'),
				label: $_('hubs.source.url'),
				component: Input,
			},
			{
				name: 'postDate',
				path: 'published_at',
				value: response.post.published_at || '',
				placeholder: $_('hubs.post.settings.date.picker.label'),
				custom:
					response.post.status === 'published' &&
					response.post.published_at !== response.post.created_at,
				label: $_('hubs.post.date'),
				setFields,
				component: SettingDatePicker,
			},
			{
				name: 'externalSource',
				path: 'etc.link_to_source',
				value: String(response.post.etc.link_to_source || false),
				placeholder: $_('hubs.link.to.source.description'),
				label: $_('hubs.external.source'),
				component: CheckBox,
			},
		];
	});
</script>

<div>
	{#if settingsForm.length}
		<form class="form" use:form>
			{#each settingsForm as setting}
				<svelte:component this={setting.component} {...setting} />
			{/each}
			<footer>
				<Button on:click={() => ($isPostSettingsModalOpen = false)} displayType="secondary"
					>{$_('common.cancel')}
				</Button>
				<Button type="submit">{$_('common.save')}</Button>
			</footer>
		</form>
	{/if}
</div>

<style>
	.form {
		display: flex;
		flex-direction: column;
		max-block-size: 480px;
		min-inline-size: 500px;
		padding-block: var(--gutterDefault) 100px /* footer height */;
		padding-inline: var(--gutterLarge);
		overflow-x: scroll;
		background-color: #eef0f1;
		gap: var(--gutterDefault);
	}

	div {
		position: relative;
	}

	footer {
		display: flex;
		position: absolute;
		justify-content: flex-end;
		inline-size: 100%;
		padding: var(--gutterDefault);
		border-block-start: var(--grayLight) 1px solid;
		inset-block-end: 0;
		inset-inline-end: 0;
		gap: 10px;
		background-color: white;
	}
</style>
